import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Warning, Lightning, TrendDown, CalendarX, ChartLineDown } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const severityConfig = {
  high: { color: 'text-destructive', bg: 'bg-destructive/10', border: 'border-destructive/30', label: 'HIGH' },
  medium: { color: 'text-chart-4', bg: 'bg-chart-4/10', border: 'border-chart-4/30', label: 'MED' },
  low: { color: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border', label: 'LOW' },
};

const patternIcons = {
  threshold_breach: <TrendDown size={14} weight="bold" />,
  consecutive_decline: <ChartLineDown size={14} weight="bold" />,
  yoy_decline: <CalendarX size={14} weight="bold" />,
  below_average: <Lightning size={14} weight="bold" />,
};

const SmartAlerts = ({ onClientClick }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/alerts/smart`, { withCredentials: true });
      setAlerts(data.alerts || []);
    } catch (err) { console.error(err.message);
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  if (loading) return <div className="text-center py-8 text-muted-foreground">Analyzing patterns...</div>;

  if (alerts.length === 0) {
    return (
      <div className="bg-card border border-border rounded-sm p-8 text-center text-muted-foreground">
        <Warning size={40} weight="thin" className="mx-auto mb-3 opacity-40" />
        <div className="text-sm">No alerts detected. All clients are performing within expected parameters.</div>
      </div>
    );
  }

  const highCount = alerts.filter(a => a.severity === 'high').length;
  const medCount = alerts.filter(a => a.severity === 'medium').length;

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Warning size={20} weight="fill" className="text-destructive" />
          <span className="text-sm font-semibold">{alerts.length} Smart Alerts</span>
        </div>
        {highCount > 0 && <span className="text-xs px-2 py-0.5 rounded-sm bg-destructive/10 text-destructive font-bold">{highCount} HIGH</span>}
        {medCount > 0 && <span className="text-xs px-2 py-0.5 rounded-sm bg-chart-4/10 text-chart-4 font-bold">{medCount} MEDIUM</span>}
      </div>

      {/* Alert cards */}
      <div className="space-y-2">
        {alerts.slice(0, 20).map((alert) => {
          const sev = severityConfig[alert.severity] || severityConfig.low;
          return (
            <div
              key={alert.client_id}
              data-testid={`smart-alert-${alert.client_id}`}
              className={`bg-card border ${sev.border} rounded-sm p-4 cursor-pointer hover:bg-muted/50 transition-colors`}
              onClick={() => onClientClick && onClientClick(alert.client_id)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${sev.bg} ${sev.color}`}>{sev.label}</span>
                  <span className="font-semibold text-sm">{alert.client_name}</span>
                </div>
                <div className="text-xs text-muted-foreground">{alert.latest_month}</div>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                <span>Current: ₹{alert.latest_revenue.toLocaleString()}</span>
                <span>Previous: ₹{alert.prev_revenue.toLocaleString()}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {alert.patterns.map((p, i) => (
                  <div key={i} className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-sm ${severityConfig[p.severity]?.bg || 'bg-muted'} ${severityConfig[p.severity]?.color || 'text-muted-foreground'}`}>
                    {patternIcons[p.type]}
                    <span>{p.message}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SmartAlerts;
